
import {
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import LanguageMultiDropdown from '@/components/fields/LanguageMultiDropdown.vue';
import CountryMultiDropdown from '@/components/fields/CountryMultiDropdown.vue';
import SpmDateRangePicker from '@/components/SpmUI/SpmDateRangePicker.vue';
import type { SpmFieldConfig } from '../../types';
import type { SpmActiveFilter, SpmFilterOption } from '../types';
import {
  SpmFilterOperator,
  SpmFilterType,
  isRangeOperator,
} from '../operators';
import {
  getFilterType,
  getAvailableOperators,
  getDefaultOperator,
  isDateRangeFilterValue,
} from '../utils';

export default defineComponent({
  name: 'SpmFilterInput',

  components: {
    Dropdown,
    MultiSelect,
    InputText,
    InputNumber,
    Button,
    LanguageMultiDropdown,
    CountryMultiDropdown,
    SpmDateRangePicker,
  },

  props: {
    fieldConfig: {
      type: Object as PropType<SpmFieldConfig>,
      required: true,
    },

    modelValue: {
      type: Object as PropType<SpmActiveFilter | null>,
      default: null,
    },

    label: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'remove'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Get filter configuration
    const filterType = computed<SpmFilterType>(() => getFilterType(props.fieldConfig.filterConfig));

    // Available operators for this filter type
    const availableOperators = computed(() => getAvailableOperators(props.fieldConfig.filterConfig));

    // Show operator selector for text and number inputs
    const showOperatorSelector = computed(() => filterType.value === 'textInput' || filterType.value === 'numberInput');

    // Operator options for dropdown
    const operatorOptions = computed(() => availableOperators.value.map((op) => ({
      value: op,
      label: t(`spmTableApi.filters.operators.${op}`),
    })));

    // Selected operator
    const selectedOperator = ref<SpmFilterOperator>(
      props.modelValue?.operator ?? getDefaultOperator(props.fieldConfig.filterConfig),
    );

    // Filter value
    const filterValue = ref<unknown>(props.modelValue?.values ?? null);

    // Range values for BETWEEN operators
    const rangeStart = ref<number | null>(null);
    const rangeEnd = ref<number | null>(null);

    // Check if current operator is a range operator
    const isRangeOperatorSelected = computed(() => isRangeOperator(selectedOperator.value));

    // Resolved options (handle async options)
    const resolvedOptions = ref<SpmFilterOption[]>([]);

    // Load options on mount
    onMounted(async () => {
      const options = props.fieldConfig.filterConfig?.options;
      if (options) {
        if (typeof options === 'function') {
          resolvedOptions.value = await options();
        } else {
          resolvedOptions.value = options;
        }
      }
    });

    // Initialize values from modelValue (handles dateRange, range operators, and regular values)
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          selectedOperator.value = newVal.operator;

          // Handle dateRange filter type - values is a DateRangeFilterValue object
          if (filterType.value === 'dateRange' && isDateRangeFilterValue(newVal.values)) {
            filterValue.value = newVal.values;
            return;
          }

          // Handle range operators (BETWEEN) for number inputs
          if (isRangeOperator(newVal.operator) && Array.isArray(newVal.values)) {
            rangeStart.value = newVal.values[0] as number ?? null;
            rangeEnd.value = newVal.values[1] as number ?? null;
          } else {
            filterValue.value = newVal.values;
          }
        }
      },
      { immediate: true },
    );

    // Emit changes when operator or value changes
    const emitChange = () => {
      let values: unknown;

      // For dateRange, the filterValue is already a DateRangeFilterValue object
      if (filterType.value === 'dateRange') {
        values = filterValue.value;
        // Don't emit if the dateRange is empty (interval === 'empty')
        if (isDateRangeFilterValue(values) && values.interval === 'empty') {
          emit('update:modelValue', null);
          return;
        }
      } else if (isRangeOperatorSelected.value) {
        values = [rangeStart.value, rangeEnd.value];
      } else {
        values = filterValue.value;
      }

      const filter: SpmActiveFilter = {
        field: props.fieldConfig.name,
        operator: selectedOperator.value,
        values,
        relationFilter: props.fieldConfig.relationFilterConfig,
      };

      emit('update:modelValue', filter);
    };

    // Watch for changes
    watch(selectedOperator, emitChange);
    watch(filterValue, emitChange);
    watch(rangeStart, emitChange);
    watch(rangeEnd, emitChange);

    return {
      t,
      filterType,
      showOperatorSelector,
      operatorOptions,
      selectedOperator,
      filterValue,
      rangeStart,
      rangeEnd,
      isRangeOperatorSelected,
      resolvedOptions,
    };
  },
});
